import React from "react";
import { Link } from "react-router-dom";
import { MapPin, Phone, Mail, Clock, ShieldCheck, ArrowRight, Facebook, Linkedin, Instagram, Globe } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { BRAND, NAV, SOCIAL } from "@/data/content";
import logo from "@/assets/tanseeq-logo.png";

export const Footer = () => {
  const { t, isAr } = useLang();
  const year = new Date().getFullYear();
  const Arrow = () => <ArrowRight className={`h-4 w-4 ${isAr ? "rotate-180" : ""}`} />;

  return (
    <footer className="bg-navy-deep text-white" data-testid="site-footer">
      <div className="container-tnsq pt-20 pb-10">
        <div className="grid lg:grid-cols-[1.2fr_1fr] gap-10 items-end pb-14 border-b border-white/10">
          <div>
            <p className="kicker text-gold mb-4">{t({ en: "Get in touch", ar: "لنتحدث" })}</p>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-[3.4rem] leading-[1.08] text-white">
              {t({ en: "Let's put your numbers", ar: "لنضع أرقامك" })}
              <br />
              <span className="text-gold">{t({ en: "in perfect order.", ar: "في نصابها الصحيح." })}</span>
            </h2>
          </div>
          <div className="flex flex-col sm:flex-row lg:flex-col gap-3">
            <a
              href={`https://wa.me/${BRAND.phoneRaw}`}
              target="_blank" rel="noopener noreferrer"
              data-testid="footer-whatsapp"
              className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-full bg-brandgreen hover:bg-brandgreen-600 font-semibold transition-colors"
            >
              {t({ en: "WhatsApp TANSEEQ", ar: "راسلنا على واتساب" })}
            </a>
            <Link
              to="/#contact"
              data-testid="footer-contact-cta"
              className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-full bg-white text-navy-deep hover:bg-gold font-semibold transition-colors"
            >
              {t({ en: "Send a message", ar: "أرسل رسالة" })} <Arrow />
            </Link>
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-[1.4fr_1fr_1.3fr] gap-10 pt-14">
          <div>
            <img src={logo} alt={t(BRAND.name)} className="h-14 w-auto bg-white rounded-lg p-2 mb-5" />
            <p className="text-white/55 text-sm leading-relaxed max-w-sm">
              {t({
                en: "Tax advisory, accounting and compliance support for businesses across the UAE — built on accurate records and clear next steps.",
                ar: "استشارات ضريبية ومحاسبة ودعم امتثال للأعمال في مختلف إمارات الدولة، بالاعتماد على سجلات دقيقة وخطوات واضحة.",
              })}
            </p>
            <div className="mt-6 inline-flex items-center gap-3 rounded-xl border border-gold/30 bg-white/[0.04] px-4 py-3">
              <ShieldCheck className="h-6 w-6 text-gold" />
              <div className="leading-tight">
                <div className="text-sm font-semibold">{t(BRAND.agency)}</div>
                <div className="text-xs text-white/50" dir="ltr">FTA No. {BRAND.ftaNo}</div>
              </div>
            </div>

            <div className="mt-6 flex items-center gap-3" data-testid="footer-social">
              <a href={SOCIAL.facebook} target="_blank" rel="noopener noreferrer" aria-label="Smart Account Vision on Facebook" title="Smart Account Vision on Facebook" className="h-10 w-10 grid place-items-center rounded-full border border-white/15 hover:bg-gold hover:text-navy-deep hover:border-gold transition-colors"><Facebook className="h-4 w-4" /></a>
              <a href={SOCIAL.linkedin} target="_blank" rel="noopener noreferrer" aria-label="Smart Account Vision on LinkedIn" title="Smart Account Vision on LinkedIn" className="h-10 w-10 grid place-items-center rounded-full border border-white/15 hover:bg-gold hover:text-navy-deep hover:border-gold transition-colors"><Linkedin className="h-4 w-4" /></a>
              <a href={SOCIAL.instagram} target="_blank" rel="noopener noreferrer" aria-label="Smart Account Vision on Instagram" title="Smart Account Vision on Instagram" className="h-10 w-10 grid place-items-center rounded-full border border-white/15 hover:bg-gold hover:text-navy-deep hover:border-gold transition-colors"><Instagram className="h-4 w-4" /></a>
              <a href={SOCIAL.software} target="_blank" rel="noopener noreferrer" aria-label="SAV Accounting System" title="SAV Accounting System" className="h-10 w-10 grid place-items-center rounded-full border border-white/15 hover:bg-gold hover:text-navy-deep hover:border-gold transition-colors"><Globe className="h-4 w-4" /></a>
            </div>
            <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-white/45">
              <a href={SOCIAL.facebook} target="_blank" rel="noopener noreferrer" className="hover:text-gold">Smart Account Vision on Facebook</a>
              <a href={SOCIAL.linkedin} target="_blank" rel="noopener noreferrer" className="hover:text-gold">Smart Account Vision on LinkedIn</a>
              <a href={SOCIAL.system} target="_blank" rel="noopener noreferrer" className="hover:text-gold">sa-v.co</a>
              <a href={SOCIAL.software} target="_blank" rel="noopener noreferrer" className="hover:text-gold">sav-sys.com</a>
            </div>
          </div>

          <div>
            <h4 className="text-sm font-semibold tracking-wider uppercase text-gold/80 mb-5">{t({ en: "Explore", ar: "تصفّح" })}</h4>
            <ul className="space-y-3">
              {NAV.map((item) => (
                <li key={item.key}>
                  {item.external ? (
                    <a href={item.href} target="_blank" rel="noopener noreferrer" data-testid={`footer-nav-${item.key}`} className="text-white/70 hover:text-gold transition-colors text-[0.95rem]">
                      {t(item.label)}
                    </a>
                  ) : (
                    <Link to={item.to} data-testid={`footer-nav-${item.key}`} className="text-white/70 hover:text-gold transition-colors text-[0.95rem]">
                      {t(item.label)}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-sm font-semibold tracking-wider uppercase text-gold/80 mb-5">{t({ en: "Contact", ar: "التواصل" })}</h4>
            <ul className="space-y-4 text-[0.95rem]">
              <li className="flex gap-3"><MapPin className="h-5 w-5 text-gold shrink-0 mt-0.5" /><span className="text-white/70">{t(BRAND.address)}</span></li>
              <li className="flex gap-3"><Phone className="h-5 w-5 text-gold shrink-0 mt-0.5" /><a href={`tel:${BRAND.phoneRaw}`} className="text-white/70 hover:text-gold transition-colors" dir="ltr">{BRAND.phone}</a></li>
              <li className="flex gap-3"><Mail className="h-5 w-5 text-gold shrink-0 mt-0.5" /><a href={`mailto:${BRAND.email}`} className="text-white/70 hover:text-gold transition-colors">{BRAND.email}</a></li>
              <li className="flex gap-3"><Clock className="h-5 w-5 text-gold shrink-0 mt-0.5" /><span className="text-white/70">{t(BRAND.hours)}</span></li>
            </ul>
          </div>
        </div>

        <div className="mt-14 pt-6 border-t border-white/10 flex flex-col md:flex-row gap-3 md:items-center md:justify-between text-xs text-white/40">
          <span>© {year} {t(BRAND.name)}. {t({ en: "All rights reserved.", ar: "جميع الحقوق محفوظة." })}</span>
          <span className="max-w-xl">
            {t({
              en: "Tax information on this website is general and may change. Verify current rules with official FTA/MoF sources before acting.",
              ar: "المعلومات الضريبية على هذا الموقع عامة وقد تتغير. يُرجى التحقق من القواعد السارية عبر مصادر الهيئة ووزارة المالية الرسمية قبل اتخاذ أي إجراء.",
            })}
          </span>
        </div>
      </div>
    </footer>
  );
};
